import { EventEmitter, Injectable } from '@angular/core';
import { BaseDownloadServiceWithFilter } from './base.service';
import { WorkTime } from '@cl/shared/models/work-times/work-time.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { StartStopWorkTimeRequest } from '@cl/shared/models/work-times/start-stop-work-time-request.model';
import { CommandResult } from '@cl/shared/models';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzImageService } from 'ng-zorro-antd/image';
import { WorkTimeFilter } from '@cl/shared/models/work-times/work-time-filter.model';
import { UpsertWorkTimeComponent } from '@cl/shared/components/upsert-work-time/upsert-work-time.component';

@Injectable({
    providedIn: 'root',
})
export class WorkTimesService extends BaseDownloadServiceWithFilter<WorkTime, WorkTimeFilter> {
    activeWorkTime: WorkTime;

    activeWorkTimeChanged: EventEmitter<WorkTime> = new EventEmitter<WorkTime>();

    constructor(
        protected httpClient: HttpClient,
        protected imageService: NzImageService,
        protected modalService: NzModalService,
    ) {
        super(httpClient, '/api/v1/work-times/', imageService);
    }

    async getActiveWorkTime(): Promise<CommandResult<WorkTime>> {
        return lastValueFrom(this.httpClient.get<CommandResult<WorkTime>>(`${this.baseApiUrl}active`));
    }

    async startWorkTimer(requestModel: StartStopWorkTimeRequest): Promise<CommandResult<WorkTime>> {
        return lastValueFrom(this.httpClient.post<CommandResult<WorkTime>>(`${this.baseApiUrl}start`, requestModel));
    }

    async stopWorkTimer(requestModel: StartStopWorkTimeRequest): Promise<void> {
        return lastValueFrom(this.httpClient.put<void>(`${this.baseApiUrl}stop`, requestModel));
    }

    async getWorkTimeIsActive(workTimeId: number): Promise<boolean> {
        return lastValueFrom(this.httpClient.get<boolean>(`${this.baseApiUrl}is-active/${workTimeId}`));
    }

    async downloadWorkTimeReport(filterObj: WorkTimeFilter): Promise<void> {
        let httpParams = new HttpParams();
        if (filterObj) {
            Object.entries(filterObj).forEach(([key, value]) => {
                if (value) {
                    httpParams = httpParams.append(key, value.toString());
                }
            });
        }
        return this.handleGeneratedFileDownload(
            lastValueFrom(
                this.httpClient.get<Blob>(`${this.baseApiUrl}report`, { params: httpParams, responseType: 'blob' as 'json', observe: 'response' }),
            ),
        );
    }

    setActiveWorkTime(workTime: WorkTime): void {
        this.activeWorkTime = workTime;
        this.activeWorkTimeChanged.emit(this.activeWorkTime);
    }

    async createNotesModal(workTime: WorkTime): Promise<NzModalRef<UpsertWorkTimeComponent>> {
        if (!(await this.getWorkTimeIsActive(workTime.id))) {
            const confirmModal: NzModalRef = this.modalService.confirm({
                nzTitle: 'Šī laika uzskaite jau ir apturēta!',
                nzCancelText: null,
                nzOkText: 'Aizvērt',
            });

            confirmModal.afterClose.subscribe(() => {
                this.setActiveWorkTime(null);
            });
            return null;
        }

        const modal: NzModalRef<UpsertWorkTimeComponent> = this.modalService.create({
            nzTitle: 'Piezīmju/apraksta ievadīšana',
            nzContent: UpsertWorkTimeComponent,
        });

        modal.afterOpen.subscribe((): void => {
            modal.getContentComponent().setEditMode(workTime);
            modal.getContentComponent().stopTimerMode = true;
            modal.getContentComponent().returnObject = true;
        });

        return modal;
    }

    getDisplaySeconds(wt: WorkTime, nowMs: number): number {
        // Stopped entry: just use elapsedSeconds
        if (wt.endDateTimeUtc) {
            return Math.max(0, wt.elapsedSeconds ?? 0);
        }

        // Active entry: elapsedSeconds so far + (now - start)
        const startMs: number = Date.parse(wt.startDateTimeUtc);
        const runningSeconds: number = Math.floor(Math.max(0, nowMs - startMs) / 1000);

        return Math.max(0, (wt.elapsedSeconds ?? 0) + runningSeconds);
    }
}
